import type { RatesSettings, TariffCategory } from '@/settings/types';

export interface BlockBreakdownRow {
  categoryId: TariffCategory['id'];
  fromUnits: number;
  toUnits: number;
  unitsInBlock: number;
  energyRateLkr: number;
  energyLkr: number;
}

export interface BillBreakdown {
  units: number;
  category: TariffCategory;
  energyLkr: number;
  fixedChargeLkr: number;
  /** Import charge + fixed charge, before SSCL. */
  subtotalLkr: number;
  /** SSCL amount computed as inclusive: base × rate / (100 − rate). */
  ssclLkr: number;
  ssclRatePercent: number;
  /** What the customer actually pays = subtotal + sscl. */
  totalLkr: number;
  blocks: BlockBreakdownRow[];
}

/**
 * SSCL is inclusive: the government defines it as 2.5% of the total amount
 * the customer pays. So SSCL = base × rate / (100 − rate).
 */
export function calculateSscl(baseLkr: number, ratePercent: number): number {
  if (ratePercent <= 0) return 0;
  return (baseLkr * ratePercent) / (100 - ratePercent);
}

/** Inverse of the inclusive-SSCL formula: given grand total, recover subtotal. */
export function subtotalFromGrandTotal(grandTotalLkr: number, ratePercent: number): number {
  if (ratePercent <= 0) return grandTotalLkr;
  return grandTotalLkr * (1 - ratePercent / 100);
}

/**
 * Pick the category by total monthly units. Cats have an implicit gap between
 * Cat A (0–60) and Cat B (61–180) — a household using 60.5 kWh (from a reverse
 * lookup) should land in the next-up category. So use maxUnits as the picker
 * and ignore the gapped minUnits.
 */
function pickCategory(units: number, rates: RatesSettings): TariffCategory {
  for (const cat of rates.tariff.categories) {
    if (cat.maxUnits === null || units <= cat.maxUnits) return cat;
  }
  return rates.tariff.categories[rates.tariff.categories.length - 1];
}

function chargeForUnitsInCategory(units: number, category: TariffCategory): {
  energyLkr: number;
  fixedChargeLkr: number;
  blocks: BlockBreakdownRow[];
} {
  let energyLkr = 0;
  let fixedChargeLkr = 0;
  const blocks: BlockBreakdownRow[] = [];

  for (const block of category.blocks) {
    const blockTo = block.toUnits ?? Number.POSITIVE_INFINITY;
    if (units <= block.fromUnits) break;
    const upperInBlock = Math.min(units, blockTo);
    const unitsInBlock = upperInBlock - block.fromUnits;
    if (unitsInBlock <= 0) continue;
    const chargeInBlock = unitsInBlock * block.energyRateLkr;
    energyLkr += chargeInBlock;
    blocks.push({
      categoryId: category.id,
      fromUnits: block.fromUnits,
      toUnits: upperInBlock,
      unitsInBlock,
      energyRateLkr: block.energyRateLkr,
      energyLkr: chargeInBlock,
    });
    if (block.fixedChargeLkr != null) {
      fixedChargeLkr = block.fixedChargeLkr;
    }
  }

  return { energyLkr, fixedChargeLkr, blocks };
}

export function calculateBill(units: number, rates: RatesSettings): BillBreakdown {
  const safeUnits = Math.max(0, units);
  const category = pickCategory(safeUnits, rates);
  const { energyLkr, fixedChargeLkr, blocks } = chargeForUnitsInCategory(safeUnits, category);
  const subtotalLkr = energyLkr + fixedChargeLkr;
  const ssclRatePercent = rates.sscl?.ratePercent ?? 0;
  const ssclLkr = calculateSscl(subtotalLkr, ssclRatePercent);
  return {
    units: safeUnits,
    category,
    energyLkr,
    fixedChargeLkr,
    subtotalLkr,
    ssclLkr,
    ssclRatePercent,
    totalLkr: subtotalLkr + ssclLkr,
    blocks,
  };
}

export interface ReverseLookupResult {
  units: number;
  bill: number;
  snapped: boolean;
}

/**
 * Given the customer's bill amount (grand total, WITH SSCL — the number shown
 * at the bottom of a CEB bill), find the monthly units that produce it. The
 * bill function is non-decreasing but has jump discontinuities at category
 * boundaries (60→61 kWh and 180→181 kWh). Bill values that fall inside a jump
 * are structurally unattainable — in that case we snap to the closer side.
 */
export function unitsForBill(targetBill: number, rates: RatesSettings): ReverseLookupResult {
  if (targetBill <= 0) return { units: 0, bill: 0, snapped: false };

  const MAX_UNITS = 10_000;
  const maxBill = calculateBill(MAX_UNITS, rates).totalLkr;
  if (targetBill >= maxBill) {
    return { units: MAX_UNITS, bill: maxBill, snapped: targetBill > maxBill };
  }

  let lo = 0;
  let hi = MAX_UNITS;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    const bMid = calculateBill(mid, rates).totalLkr;
    if (bMid <= targetBill) lo = mid;
    else hi = mid;
  }

  const billAtLo = calculateBill(lo, rates).totalLkr;
  const billAtHi = calculateBill(hi, rates).totalLkr;
  const distToLo = Math.abs(billAtLo - targetBill);
  const distToHi = Math.abs(billAtHi - targetBill);

  const TOL = 0.02;
  if (distToLo <= TOL) return { units: lo, bill: billAtLo, snapped: false };
  if (distToHi <= TOL) return { units: hi, bill: billAtHi, snapped: false };

  // Neither side matches within tolerance → we're straddling a jump. Snap.
  if (distToLo <= distToHi) return { units: lo, bill: billAtLo, snapped: true };
  return { units: hi, bill: billAtHi, snapped: true };
}
